// src/pages/private/settings/securityTabs/SecurityCredential.jsx

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../../../../authContext';
import { isValidPassword } from '../../../../../utils/validator';

function RenderWithNewlines({ text }) {
  if (typeof text !== 'string') return null;

  const lines = text.split('\n');
  return (
      <div>
          {lines.map((line, index) => (
              <div key={index}>{line}</div>
          ))}
      </div>
  );
}

function ErrorMessage({ message }) {
  if (message) {
      return <p className="security-card__alert">{message}</p>;
  }
  return <div className="security-card__alert-spacer" />;
}

function SecurityCredential() {
  const { t } = useTranslation();
  const { email, getUserInfo } = useAuth();
  const [newEmail, setNewEmail] = useState('');
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  const toggleTooltip = () => {
    setShowTooltip(prev => !prev);
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/email/change', { email: newEmail }, { withCredentials: true });
      setConfirmationMessage(t('EmailSent'));
      getUserInfo();
    } catch (error) {
      console.error(t('ErrorChangingEmail'), error);
      setConfirmationMessage(t('ErrorChangingEmail'));
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError(t('error_passwordsDontMatch'));
      return;
    }

    if (!isValidPassword(newPassword) || !isValidPassword(confirmPassword)) {
      setPasswordError(t('error_passwordCriteria'));
      return;
    }

    try {
      await axios.post('/api/password/change', { password: newPassword }, { withCredentials: true });
      alert(t('PassChangeSuccess'));
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    } catch (error) {
      console.error('Error changing password:', error);
      alert(t('ErrorChangePass'));
    }
  };

  return (
    <section className="security-credentials" aria-labelledby="security-credentials-heading">
      <header className="user-settings-section security-page__section-heading">
        <h3 id="security-credentials-heading" className="settings-h3">
          {t('SecuritySettingscredentials')}
        </h3>
      </header>

      <form className="security-card-form" onSubmit={handleEmailChange}>
        <div className="security-card security-card--email">
          <div className="security-card__lead" aria-hidden={false}>
            <div className="security-card__icon-wrap">
              <MailOutlined className="security-card__icon" aria-hidden />
            </div>
            <div className="security-card__copy">
              <h4 className="security-card__title" id="security-email-title">
                {t('securityCardEmailTitle')}
              </h4>
              <p className="security-card__subtitle">{t('securityCardEmailDesc')}</p>
            </div>
          </div>
          <div className="security-card__fields">
            <label className="visually-hidden" htmlFor="security-email-input">{t('Email')}</label>
            <input
              id="security-email-input"
              className="security-card__input security-card__input--email"
              type="email"
              placeholder={email}
              value={newEmail}
              onChange={(ev) => setNewEmail(ev.target.value)}
              autoComplete="email"
            />
          </div>
          <div className="security-card__cta">
            <button type="submit" className="btn-style1 btn-security-submit">
              {t('Save email')}
            </button>
          </div>
        </div>
        <ErrorMessage message={confirmationMessage} />
      </form>

      <form className="security-card-form" onSubmit={handlePasswordChange}>
        <div className="security-card security-card--password">
          <div className="security-card__lead">
            <div className="security-card__icon-wrap">
              <LockOutlined className="security-card__icon" aria-hidden />
            </div>
            <div className="security-card__copy">
              <h4 className="security-card__title">{t('securityCardPasswordTitle')}</h4>
              <p className="security-card__subtitle">{t('securityCardPasswordDesc')}</p>
            </div>
          </div>
          <div className="security-card__fields security-card__fields--password">
            <input
              type="text"
              name="email"
              value={email}
              aria-hidden="true"
              hidden
              autoComplete="email"
              readOnly
            />
            <div className="security-card__password-bundle">
              <div className="security-card__password-row">
                <label className="visually-hidden" htmlFor="security-new-password">{t('Password')}</label>
                <input
                  id="security-new-password"
                  className="security-card__input"
                  type="password"
                  placeholder={t('Password')}
                  value={newPassword}
                  onChange={(ev) => setNewPassword(ev.target.value)}
                  autoComplete="new-password"
                />
                <label className="visually-hidden" htmlFor="security-confirm-password">{t('ConfirmPassword')}</label>
                <input
                  id="security-confirm-password"
                  className="security-card__input"
                  type="password"
                  placeholder={t('ConfirmPassword')}
                  value={confirmPassword}
                  onChange={(ev) => setConfirmPassword(ev.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <button
                type="button"
                className="security-card__info-btn"
                onClick={toggleTooltip}
                aria-expanded={showTooltip}
                aria-label={t('passwordTipAria')}
              >
                <span aria-hidden className="security-card__info-glyph">i</span>
              </button>
            </div>
            {showTooltip && (
              <div className="security-card__tooltip" role="tooltip">
                <RenderWithNewlines text={t('passwordTip')} />
              </div>
            )}
          </div>
          <div className="security-card__cta">
            <button type="submit" className="btn-style1 btn-security-submit">
              {t('Save password')}
            </button>
          </div>
        </div>
        <ErrorMessage message={passwordError} />
      </form>
    </section>
  );
}

export default SecurityCredential;
